import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormBuilder,
  Validators,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { DirectivesModule } from '@et/directives';
import { ProfileDto } from '@escrowtab/v2profile-api-client';
import { PresentationalAtomsModule } from '@et/presentational/atoms';
import { Packet } from '@escrowtab/packets-api-client';
import {
  allStates,
  EtjournalPacketEntry,
  AddPacketJournalDto,
} from '@et/typings';

@Component({
  selector: 'et-journal-journal-form',
  imports: [PresentationalAtomsModule, ReactiveFormsModule, DirectivesModule],
  templateUrl: './journal-form.component.html',
  styleUrl: './journal-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JournalFormComponent implements OnInit {
  @Input({ required: true }) packet!: Packet;
  @Input({ required: true }) profile!: ProfileDto;
  @Input() eJournal: EtjournalPacketEntry | null = null;
  @Input() hideButtons = false;

  @Input() defaultName = '';

  @Input() isLoading = false;
  @Input() error: string | null = null;

  @Output() payloadSubmitted = new EventEmitter<
    AddPacketJournalDto | EtjournalPacketEntry
  >();

  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);

  readonly stateOptions = allStates;

  form = this.fb.nonNullable.group({
    id: [''],
    profileId: [''],
    packetId: [''],
    packetName: ['', [Validators.required]],
    notaryName: ['', [Validators.required]],
    state: ['', [Validators.required]],
    feeCharged: [''],
    utcTimeOfSigning: [''],
    closingType: ['IPEN'],
    platform: ['EscrowTab'],
    isFinal: [false],
    uniqueJournalId: [''],
  });

  /**
   * Whether the dialog is editing an existing eJournal packet entry.
   * Derived from the current `AddEjournalService.eJournal()` value.
   */
  get isEditMode(): boolean {
    return !!this.eJournal;
  }

  /**
   * Initializes the form using the currently selected packet/profile
   * and any existing eJournal entry (edit mode).
   */
  ngOnInit(): void {
    this.patchFormFromService(this.packet, this.profile, this.eJournal);
  }

  /**
   * Validates the form and submits the create/update request.
   * Normalizes `feeCharged` from dollars to cents before sending to the API.
   */
  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    const fees = raw.feeCharged.replace(/[^0-9.]/g, '');
    const payload = {
      ...raw,
      feeCharged: Number(fees),
    };

    this.payloadSubmitted.emit(payload);
  }

  showValidationErrors() {
    this.form.markAllAsTouched();
    this.cdr.detectChanges();
  }

  /**
   * Patches the reactive form from the current service state.
   * In create mode, removes server-only controls (`id`, `uniqueJournalId`).
   */
  private patchFormFromService(
    packet: Packet,
    profile: ProfileDto,
    eJournal: EtjournalPacketEntry | null,
  ): void {
    if (eJournal) {
      const feeCharged = Number.isFinite(eJournal.feeCharged)
        ? eJournal.feeCharged
        : '';

      this.form.patchValue({
        id: eJournal.id,
        profileId: profile.profileId!,
        packetId: eJournal.packetId,
        packetName: packet.packetName!,
        notaryName: eJournal.notaryName ?? '',
        state: eJournal.state,
        feeCharged: feeCharged.toString(),
        closingType: eJournal.closingType,
        platform: eJournal.platform ?? 'EscrowTab',
        isFinal: !!eJournal.isFinal,
        uniqueJournalId: eJournal.uniqueJournalId ?? '',
      });

      if (eJournal.utcTimeOfSigning) {
        this.form.patchValue({
          utcTimeOfSigning: eJournal.utcTimeOfSigning,
        });
      }
      return;
    }

    this.form.patchValue({
      profileId: profile.profileId!,
      packetId: packet.packetId!,
      packetName: packet.packetName!,
      notaryName: this.defaultName,
      state: packet.packetState!,
      feeCharged: '',
      isFinal: false,
    });

    // If packet is not signed, skip signing date and add in on journal finalization
    if (packet.utcDateSigned) {
      const utcDate = new Date(packet.utcDateSigned! + 'Z');
      this.form.patchValue({
        utcTimeOfSigning: utcDate.toISOString(),
      });
    }

    // Remove ID control on a new eJournal
    (this.form as FormGroup).removeControl('id', { emitEvent: false });
    (this.form as FormGroup).removeControl('uniqueJournalId', {
      emitEvent: false,
    });
  }
}
